import { uiText } from "@/lib/i18n/uiText";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Activity, GitBranch, Gauge, ShieldCheck } from "lucide-react";
import { usePredictionDiagnostics } from "@/hooks/usePredictionDiagnostics";
import type { Prediction } from "@/types";
import { useLocalizedList } from "@/hooks/useLocalizedContent";

interface PredictionDiagnosticsPanelProps {
  prediction: Prediction;
}

const PATTERN_TYPE_COLORS: Record<string, string> = {
  underdog_form: "bg-amber-500/10 border-amber-500/30 text-amber-600",
  league_tendency: "bg-blue-500/10 border-blue-500/30 text-blue-600",
  goal_pattern: "bg-purple-500/10 border-purple-500/30 text-purple-600",
  home_away_anomaly: "bg-cyan-500/10 border-cyan-500/30 text-cyan-600",
};

/**
 * Diagnostics panel: shows the strategies that actually fired for a prediction,
 * each mapped to its pattern_type, score contribution and reasoning, plus the
 * key feature signals (cumulative score, win probability, safety rating).
 */
export function PredictionDiagnosticsPanel({ prediction }: PredictionDiagnosticsPanelProps) {
  const { data, isLoading } = usePredictionDiagnostics(prediction.id);

  const signals = prediction.strategy_signals;
  // Members can't read the admin-only trigger table, so fall back to the
  // per-strategy details already stored on the unlocked prediction.
  const detailTriggers = (() => {
    const raw = (signals as unknown as { strategy_details?: unknown })?.strategy_details;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((d: Record<string, unknown>) => ({
        strategy_name: String(d["name"] ?? d["strategy_name"] ?? d["strategy"] ?? ""),
        score_contribution: Number(d["score"] ?? d["score_contribution"] ?? d["contribution"] ?? d["weight"] ?? 0) || 0,
        reasoning: (d["reasoning"] ?? d["reason"] ?? d["explanation"] ?? null) as string | null,
        pattern_type: (d["pattern_type"] ?? d["category"] ?? null) as string | null,
        pattern_name: null,
        success_rate: d["success_rate"] != null ? Number(d["success_rate"]) : null,
      }))
      .filter((t) => t.strategy_name)
      .sort((a, b) => b.score_contribution - a.score_contribution);
  })();
  const storedTriggers = (data?.triggers?.length ? data.triggers : detailTriggers);
  const reasoning = useLocalizedList(storedTriggers.flatMap((t, i) => t.reasoning ? [{
    entityType: "prediction-strategy", entityId: `${prediction.id}:${i}`, field: "reasoning", sourceText: t.reasoning,
  }] : []));
  const hasStored = storedTriggers.length > 0;

  // Fallback list when no stored trigger rows exist.
  const fallbackStrategies = signals?.triggered_strategies ?? [];

  const maxContribution = Math.max(1, ...storedTriggers.map((t) => Math.abs(t.score_contribution)));

  return (
    <div className="space-y-4">
      {/* Key feature signals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <SignalStat
          icon={Activity}
          label={uiText("Triggered")}
          value={hasStored ? storedTriggers.length : fallbackStrategies.length}
        />
        <SignalStat icon={Gauge} label={uiText("Cumulative")} value={signals?.cumulative_score ?? "—"} />
        <SignalStat
          icon={GitBranch}
          label={uiText("Win Prob")}
          value={signals?.probability_estimate != null ? `${signals.probability_estimate}%` : "—"}
        />
        <SignalStat icon={ShieldCheck} label={uiText("Safety")} value={signals?.safety_rating ?? "—"} />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />  {uiText("Loading diagnostics…")} </div>
      )}

      {/* Stored trigger rows with pattern types + contributions */}
      {!isLoading && hasStored && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{uiText("Triggered Strategies & Pattern Types")}</h4>
          {storedTriggers.map((t, i) => (
            <div key={`${t.strategy_name}-${i}`} className="rounded-lg border border-border/60 p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium">{uiText(t.strategy_name)}</span>
                <div className="flex items-center gap-1.5">
                  {t.pattern_type && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${PATTERN_TYPE_COLORS[t.pattern_type] || ""}`}
                    >
                      {uiText(t.pattern_type.replace(/_/g, " "))}
                    </Badge>
                  )}
                  {t.success_rate != null && (
                    <Badge variant="secondary" className="text-[10px]">
                      {Math.round(t.success_rate)}{uiText("% hist")} </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Progress
                  value={(Math.abs(t.score_contribution) / maxContribution) * 100}
                  className="h-1.5 flex-1"
                />
                <span className="text-xs font-medium tabular-nums w-12 text-right">
                  {t.score_contribution > 0 ? "+" : ""}
                  {t.score_contribution.toFixed(1)}
                </span>
              </div>
              {t.reasoning && (
                <p className="text-xs text-muted-foreground">{reasoning.map.get(`${prediction.id}:${i}::reasoning`)}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fallback: no stored rows */}
      {!isLoading && !hasStored && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{uiText("Triggered Strategies")}</h4>
          {fallbackStrategies.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {fallbackStrategies.map((s, i) => (
                <Badge key={`${s}-${i}`} variant="outline" className="text-xs">
                  {uiText(s)}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
               {uiText("No detailed strategy diagnostics were recorded for this prediction.")} </p>
          )}
        </div>
      )}
    </div>
  );
}

function SignalStat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Activity;
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-lg bg-muted/40 p-2.5 text-center">
      <Icon className="h-3.5 w-3.5 mx-auto mb-1 text-primary" />
      <p className="text-sm font-bold leading-none">{typeof value === "string" ? uiText(value) : value}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
